import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PublicGrid from "@/components/PublicGrid";
import { Eye, EyeOff } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function Login() {
  const nav = useNavigate();
  const { login, user } = useAuth();
  useEffect(() => { if (user) nav("/projects", { replace: true }); }, [user, nav]);
  const [step, setStep] = useState("creds"); // creds | otp
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [masked, setMasked] = useState("");
  const [loading, setLoading] = useState(false);

  const submitCreds = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      toast.error("Enter email/username and password");
      return;
    }
    setLoading(true);
    try {
      const device_token = localStorage.getItem("ax_device") || undefined;
      const { data } = await api.post("/auth/login", {
        identifier: identifier.trim(), password, device_token,
      });
      if (data.step === "authenticated") {
        login(data.token, null, data.user);
        toast.success(`Welcome back, ${data.user.display}`);
        nav("/projects");
      } else {
        setChallengeId(data.challenge_id);
        setMasked(data.masked_email);
        setStep("otp");
        toast.success(`Code sent to ${data.masked_email}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { challenge_id: challengeId, code: otp });
      login(data.token, data.device_token, data.user);
      toast.success(`Device trusted for 12 hours`);
      nav("/projects");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex flex-col-reverse lg:flex-row relative z-[1] bg-transparent text-[#F5F5F7]">
      {/* Public showcase grid */}
      <div className="lg:w-[62%] lg:h-screen lg:overflow-y-auto border-r border-zinc-800/70 relative" data-testid="login-feed">
        <div className="sticky top-0 z-10 px-5 py-4 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/90 to-transparent flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="login-home-link"><Logo size={32} showName={false} /><span className="font-heading font-bold text-lg tracking-tight">Ai Xtudio</span></Link>
          <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Community showcase</span>
        </div>
        <div className="px-5 pb-10">
          <PublicGrid compact emptyHint="Published work from the studio will appear here." />
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-[38%] lg:h-screen lg:sticky lg:top-0 flex items-center justify-center p-6 py-12 bg-[radial-gradient(800px_400px_at_80%_10%,rgba(95,192,200,0.12),transparent_60%)]">
        <div className="w-full max-w-md ax-fade-up" data-testid="login-card">
          <div className="mb-10 lg:hidden">
            <Logo size={36} />
          </div>

          {step === "creds" ? (
            <>
              <h2 className="font-heading text-3xl font-semibold">Sign in</h2>
              <p className="text-zinc-500 text-sm mt-2">Admin access only. Use your email or username.</p>
              <form onSubmit={submitCreds} className="mt-8 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="identifier" className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Email or Username</Label>
                  <Input
                    id="identifier"
                    data-testid="login-identifier-input"
                    autoComplete="username"
                    value={identifier}
                    onChange={(e)=>setIdentifier(e.target.value)}
                    placeholder="Enter your email"
                    className="bg-[#121212] border-zinc-800 h-12 ax-focus-ring"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type={show ? "text" : "password"}
                      value={password}
                      onChange={(e)=>setPassword(e.target.value)}
                      placeholder="••••••"
                      className="bg-[#121212] border-zinc-800 h-12 pr-11 ax-focus-ring"
                    />
                    <button
                      type="button"
                      data-testid="login-password-toggle"
                      onClick={()=>setShow(s=>!s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
                    >
                      {show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="w-full h-12 btn-primary-glow text-white font-semibold text-base"
                >
                  {loading ? "Verifying…" : "Continue"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="font-heading text-3xl font-semibold">Verify it's you</h2>
              <p className="text-zinc-500 text-sm mt-2">
                We sent a 6-digit code to <span className="text-zinc-200 font-mono">{masked}</span>.
                It expires in 10 minutes.
              </p>
              <form onSubmit={submitOtp} className="mt-8 space-y-6">
                <div className="flex justify-center" data-testid="login-otp-wrap">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i=>(
                        <InputOTPSlot key={i} index={i} data-testid={`login-otp-slot-${i}`} className="h-14 w-12 text-xl bg-[#121212] border-zinc-800"/>
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  data-testid="login-verify-button"
                  className="w-full h-12 btn-primary-glow text-white font-semibold"
                >
                  {loading ? "Verifying…" : "Verify & Enter Studio"}
                </Button>
                <button
                  type="button"
                  data-testid="login-change-account-button"
                  onClick={()=>{ setStep("creds"); setOtp(""); }}
                  className="w-full text-xs font-mono text-zinc-500 hover:text-zinc-300 uppercase tracking-widest"
                >
                  Use a different account
                </button>
              </form>
            </>
          )}

          <div className="mt-10 text-xs font-mono text-zinc-600 text-center">
            Private build · Two authorized admins · OTP verified
          </div>
        </div>
      </div>
    </div>
  );
}
